import { BUSINESS_TIMEZONE } from './config.ts';

/**
 * Number and date formatting through `Intl` with the user's locale and the business time zone
 * (docs/DASHBOARD_SPECIFICATION.md §1.1). Null never becomes 0: it is shown as an em dash.
 */
export const DASH = '—';

/**
 * Uzbek separators are applied here, not taken from the browser: Chrome's and Node's Uzbek locale
 * data disagree (Chrome prints "1,237.5"), and the dashboard must read the same everywhere.
 * Uzbek groups thousands with a no-break space and uses a decimal comma.
 */
const SEPARATORS: Record<string, { group: string; decimal: string }> = {
  'uz-Latn': { group: ' ', decimal: ',' },
};

class LocalizedNumberFormat {
  private readonly format: Intl.NumberFormat;
  constructor(
    private readonly locale: string,
    options: Intl.NumberFormatOptions,
  ) {
    this.format = new Intl.NumberFormat(SEPARATORS[locale] ? 'en' : locale, options);
  }
  formatNumber(value: number): string {
    const separators = SEPARATORS[this.locale];
    if (!separators) return this.format.format(value);
    return this.format
      .formatToParts(value)
      .map((part) =>
        part.type === 'group'
          ? separators.group
          : part.type === 'decimal'
            ? separators.decimal
            : part.value,
      )
      .join('');
  }
}

const numberFormats = new Map<string, LocalizedNumberFormat>();
function numberFormat(locale: string, options: Intl.NumberFormatOptions): LocalizedNumberFormat {
  const key = `${locale}|${JSON.stringify(options)}`;
  let format = numberFormats.get(key);
  if (!format) {
    format = new LocalizedNumberFormat(locale, options);
    numberFormats.set(key, format);
  }
  return format;
}

export function formatNumber(
  value: number | null,
  locale: string,
  maximumFractionDigits = 0,
): string {
  return value === null
    ? DASH
    : numberFormat(locale, { maximumFractionDigits }).formatNumber(value);
}

/** A ratio in [0, 1] as a percentage with one decimal place; `null` (empty denominator) → "—". */
export function formatPercent(ratio: number | null, locale: string, digits = 1): string {
  return ratio === null
    ? DASH
    : numberFormat(locale, {
        style: 'percent',
        minimumFractionDigits: digits,
        maximumFractionDigits: digits,
      }).formatNumber(ratio);
}

/** A signed change: `+6`, `-3`, `0`. */
export function formatSigned(value: number, locale: string, maximumFractionDigits = 0): string {
  return numberFormat(locale, { signDisplay: 'exceptZero', maximumFractionDigits }).formatNumber(
    value,
  );
}

/** A signed percentage change (`+14,3%`). */
export function formatSignedPercent(ratio: number, locale: string): string {
  return numberFormat(locale, {
    style: 'percent',
    signDisplay: 'exceptZero',
    maximumFractionDigits: 1,
  }).formatNumber(ratio);
}

/** Percentage points, from a change in a ratio (0.031 → `+3,1`). */
export function formatPoints(ratioChange: number, locale: string): string {
  return formatSigned(ratioChange * 100, locale, 1);
}

/** `2026-10-21` → `21.10.2026`. */
export function formatDate(businessDate: string): string {
  const [year, month, day] = businessDate.split('-');
  return `${day}.${month}.${year}`;
}

const timeFormats = new Map<string, Intl.DateTimeFormat>();
function dateTimeFormat(key: string, options: Intl.DateTimeFormatOptions): Intl.DateTimeFormat {
  let format = timeFormats.get(key);
  if (!format) {
    format = new Intl.DateTimeFormat('en-GB', { timeZone: BUSINESS_TIMEZONE, ...options });
    timeFormats.set(key, format);
  }
  return format;
}

/** `HH:mm`, 24-hour, in the business time zone. */
export function formatTime(instant: string | Date): string {
  return dateTimeFormat('time', {
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).format(new Date(instant));
}

/** `21.10.2026 19:00`, in the business time zone. */
export function formatDateTime(instant: string | Date): string {
  const date = new Date(instant);
  const day = dateTimeFormat('date', { year: 'numeric', month: '2-digit', day: '2-digit' })
    .format(date)
    .replaceAll('/', '.');
  return `${day} ${formatTime(date)}`;
}

/** Minutes as `38 daq.` or, from an hour up, `2,3 soat`. */
export function formatDuration(
  minutes: number | null,
  locale: string,
  units: { minutes: string; hours: string },
): string {
  if (minutes === null) return DASH;
  if (minutes < 60) return `${formatNumber(minutes, locale)} ${units.minutes}`;
  return `${formatNumber(minutes / 60, locale, 1)} ${units.hours}`;
}

const CURRENCY_SYMBOLS: Readonly<Record<string, string>> = { USD: '$', EUR: '€' };

/**
 * Money from minor units (cents): `$1 234` — or with cents for small amounts such as a cost per
 * lead (`$17,24`). Currencies without a symbol are written after the number (`1 234 UZS`).
 */
export function formatMoney(
  minor: number | null,
  currency: string,
  locale: string,
  options: { cents?: boolean } = {},
): string {
  if (minor === null) return DASH;
  const major = minor / 100;
  const digits = (options.cents ?? Math.abs(major) < 100) ? 2 : 0;
  const number = numberFormat(locale, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).formatNumber(major);
  const symbol = CURRENCY_SYMBOLS[currency];
  return symbol ? `${symbol}${number}` : `${number} ${currency}`;
}

/** Return on ad spend as a multiple: `4,6x`. */
export function formatMultiple(value: number | null, locale: string): string {
  return value === null ? DASH : `${formatNumber(value, locale, 1)}x`;
}

/** Seconds as `29 son.` or, from 90 seconds up, `2 daq. 20 son.` */
export function formatSeconds(
  seconds: number | null,
  locale: string,
  units: { seconds: string; minutes: string },
): string {
  if (seconds === null) return DASH;
  const whole = Math.round(seconds);
  if (whole < 90) return `${formatNumber(whole, locale)} ${units.seconds}`;
  const minutes = Math.floor(whole / 60);
  const rest = whole % 60;
  return rest === 0
    ? `${formatNumber(minutes, locale)} ${units.minutes}`
    : `${formatNumber(minutes, locale)} ${units.minutes} ${rest} ${units.seconds}`;
}
